import { Box, Heading, IconButton, Link, Separator, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FaLink } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';

const SECTIONS: LearnMoreSection[] = [
  { id: 'support-level', label: 'Our level of support' },
  { id: 'contact-us', label: 'Contact us' },
];

const HandsOnSupport = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Hands-On Support' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Hands-on support
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Olympiq is built to be self-serve, but you still get real people who know the product
          inside out whenever you need a hand.
        </Text>
      </Box>

      <Separator />

      <SectionBlock id="support-level" title="Our level of support">
        <Text>
          The platform is intentionally self-sufficient, yet we appreciate that the early days of a
          new app launch - or an unexpected change - can raise questions. The fastest way to reach
          us is via the WhatsApp number listed on our{' '}
          <Link href="/contact-us" color="blue.500">
            Contact Us
          </Link>{' '}
          page; it routes straight to the team who run Olympiq day-to-day.
        </Text>
        <Text>
          We aim to acknowledge any request within 24 hours, resolve bugs within five working days,
          and accelerate major or blocking issues for a 24-hour turnaround wherever possible.
        </Text>
        <Text>
          We operate from Bristol, UK, so you always speak to someone who both understands your club
          context and can make changes immediately - no hand-offs or outsourced triage.
        </Text>
      </SectionBlock>

      <SectionBlock id="contact-us" title="Contact us">
        <Text>
          Ready to chat? Reach out through the{' '}
          <Link href="/contact-us" color="blue.500">
            Contact Us
          </Link>{' '}
          page for WhatsApp, email, or phone details and we’ll take it from there.
        </Text>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default HandsOnSupport;
